import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Input } from '@/components/ui/input'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState, useMemo } from 'react'
import { toast } from '@/hooks/use-toast'
import { publicSignup, AuthResponse, PublicSignup } from '@/api/auth'
import i18n, { loadAuthenticatedTranslations, loadLanguageFromBackend } from '@/i18n'
import { useAuthStore } from '@/stores/authStore'
import { useNavigate, Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { User, Mail, Lock, Scale, Eye, EyeOff, ArrowLeft, ArrowRight, Info, Phone, Building2, CheckCircle2, Sunrise, Sunset, Verified } from 'lucide-react'
import { cn } from '@/lib/utils'
import { LanguageSwitcher } from '@/components/language-switcher'

export default function SignupPage() {
  const { t } = useTranslation()
  const formSchema = useMemo(() => z.object({
    cabinet_name: z.string().min(1, { message: t('cabinet_name_required', { defaultValue: 'Cabinet name is required' }) }).max(128),
    name: z.string().min(1, { message: t('name_required', { defaultValue: 'Name is required' }) }).max(50),
    password: z.string().min(8, { message: t('password_min_8', { defaultValue: 'Password must be at least 8 characters' }) }),
    phone: z.string().min(1, { message: t('phone_required', { defaultValue: 'Phone number is required' }) }).max(20),
    email: z.string().email({ message: t('invalid_email') }).min(1, { message: t('email_required', { defaultValue: 'Email is required' }) }),
    availability_days: z.array(z.enum(['mon', 'tue', 'wed', 'thu', 'fri']))
      .min(1, { message: t('availability_days_required', { defaultValue: 'Select at least one day' }) }),
    availability_slot: z.enum(['morning', 'afternoon'], {
      errorMap: () => ({ message: t('availability_slot_required', { defaultValue: 'Select a time slot' }) }),
    }),
    firebase_device_id: z.string().optional(),
    timezone: z.string().optional(),
    locale: z.string().optional(),
    preferred_language: z.string().optional(),
  }), [t])

  type SignupForm = z.infer<typeof formSchema>
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const form = useForm<SignupForm>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      cabinet_name: '',
      name: '',
      password: '',
      phone: '',
      email: '',
      availability_days: [],
      availability_slot: undefined,
      firebase_device_id: '',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      locale: navigator.language,
      preferred_language: i18n.language || 'fr',
    },
  })

  const WEEKDAYS: { value: 'mon' | 'tue' | 'wed' | 'thu' | 'fri'; label: string }[] = [
    { value: 'mon', label: t('day_mon', { defaultValue: 'Mon' }) },
    { value: 'tue', label: t('day_tue', { defaultValue: 'Tue' }) },
    { value: 'wed', label: t('day_wed', { defaultValue: 'Wed' }) },
    { value: 'thu', label: t('day_thu', { defaultValue: 'Thu' }) },
    { value: 'fri', label: t('day_fri', { defaultValue: 'Fri' }) },
  ]

  const nav = useNavigate()
  const [step, setStep] = useState<number>(0)
  const totalSteps = 3


  const handleNext = async () => {
    const fields: (keyof SignupForm)[] = step === 0
      ? ['name', 'phone', 'email', 'password']
      : step === 1
        ? ['cabinet_name', 'availability_days', 'availability_slot']
        : ['preferred_language']
    const ok = await form.trigger(fields)
    if (ok) setStep(step + 1)
  }

  async function onSubmit(values: SignupForm) {
    setIsLoading(true)
    const normalizedPhone = values.phone.startsWith('0')
      ? '+212' + values.phone.slice(1)
      : values.phone
    const payload: PublicSignup = {
      cabinet_name: values.cabinet_name,
      name: values.name,
      password: values.password,
      phone: normalizedPhone,
      email: values.email,
      availability_days: values.availability_days,
      availability_slot: values.availability_slot,
      firebase_device_id: null,
      timezone: values.timezone || null,
      locale: values.locale || null,
      preferred_language: values.preferred_language || null,
    }

    try {
      const v: AuthResponse = await publicSignup(payload)
      if (v?.access) {
        const state = useAuthStore.getState()
        state.auth.setAccessToken(v.access)
        state.auth.setRefreshToken(v.refresh)
        state.auth.setUser(v.user)

        await loadLanguageFromBackend().catch(() => { })
        await loadAuthenticatedTranslations().catch(() => { })

        if (v.email_verification_required) {
          toast({
            title: t('verify_email_title'),
            description: t('verify_email_desc'),
          })
          nav({ to: '/auth/verify-email' })
        } else if (v.approval_status === 'approved') {
          nav({ to: '/dashboard' })
        } else {
          // Demande created — cabinet awaits super-admin approval
          nav({ to: '/auth/pending-approval' })
        }
      } else {
        toast({ variant: 'destructive', title: i18n.t('serv_wrong_resp') })
      }
    } catch (e: unknown) {
      const err = e as { response?: { status?: number; data?: Record<string, string | string[]> } }
      let handled = false

      if (err.response?.status === 400 && err.response.data) {
        const data = err.response.data
        let targetStep = step
        const step0Fields = ['name', 'phone', 'email', 'password']
        const step1Fields = ['cabinet_name', 'availability_days', 'availability_slot']

        Object.keys(data).forEach((key) => {
          if (key in form.getValues()) {
            const message = Array.isArray(data[key]) ? data[key][0] : data[key] as string
            form.setError(key as keyof SignupForm, {
              type: 'server',
              message: message,
            })
            handled = true

            if (step0Fields.includes(key)) targetStep = Math.min(targetStep, 0)
            else if (step1Fields.includes(key)) targetStep = Math.min(targetStep, 1)
          }
        })

        if (targetStep !== step) {
          setStep(targetStep)
        }
      }

      if (!handled) {
        const errorMsg = (err.response?.data as { error?: string })?.error ?? i18n.t('serv_error')
        toast({
          variant: 'destructive',
          title: errorMsg,
        })
      }
    } finally {
      setIsLoading(false)
    }
  }

  const isRTL = i18n.dir() === 'rtl';

  const stepTitles = [
    t('signup_step_account', { defaultValue: 'Account details' }),
    t('signup_step_cabinet', { defaultValue: 'Cabinet details' }),
    t('signup_step_preferences', { defaultValue: 'Review' })
  ]

  const STEP_ICONS = [User, Building2, CheckCircle2]

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center p-4 overflow-hidden bg-gray-50 dark:bg-slate-950 text-gray-800 dark:text-gray-100 transition-colors duration-200">
      {/* Language Switcher */}
      <div className='absolute top-4 right-4 z-10'>
        <LanguageSwitcher variant='outline' size='default' />
      </div>

      {/* Background Blobs */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10 pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute top-[40%] -left-[10%] w-[40%] h-[40%] bg-secondary/10 rounded-full blur-3xl" />
      </div>

      {/* Main Card */}
      <div className="w-full max-w-[480px] bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-gray-100 dark:border-gray-800 overflow-hidden relative">
        <div className="h-2 w-full bg-gradient-to-r from-primary to-primary-dark" />

        {/* Header Section — mirrors the login identity block */}
        <div className="px-6 pt-8 pb-2">
          <div className="flex flex-col items-center text-center mb-6">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-blue-50 dark:bg-blue-900/20 mb-3 text-primary dark:text-blue-300 shadow-sm">
              <Scale className="w-7 h-7" />
            </div>
            <h1 className="text-2xl font-extrabold tracking-wide text-primary dark:text-white font-display">
              Avocat<span className="text-secondary">Pro</span>
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {t('signup_subtitle', { defaultValue: 'Create a new cabinet account.' })}
            </p>
          </div>

          {/* Stepper — real sequence: account -> cabinet -> review */}
          <div className="flex items-center justify-center gap-1 mb-2" aria-label={t('step_of_total', { current: step + 1, total: totalSteps, defaultValue: 'Step {{current}} / {{total}}' })}>
            {[0, 1, 2].map((i) => {
              const Icon = STEP_ICONS[i]
              const isDone = i < step
              const isActive = i === step
              return (
                <div key={i} className="flex items-center flex-1 last:flex-none">
                  <div className="flex flex-col items-center gap-1.5 shrink-0">
                    <div
                      className={cn(
                        "w-9 h-9 rounded-full flex items-center justify-center border-2 transition-all duration-300",
                        isDone
                          ? "bg-secondary border-secondary text-white"
                          : isActive
                            ? "bg-primary border-primary text-white shadow-md shadow-primary/30"
                            : "bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-400"
                      )}
                    >
                      <Icon className="w-4 h-4" />
                    </div>
                    <span
                      className={cn(
                        "text-[10px] font-medium whitespace-nowrap max-w-[72px] truncate",
                        isActive ? "text-primary dark:text-blue-300" : "text-gray-400 dark:text-gray-500"
                      )}
                    >
                      {stepTitles[i]}
                    </span>
                  </div>
                  {i < 2 && (
                    <div className="flex-1 h-[2px] mx-1 -mt-5 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700">
                      <div
                        className={cn(
                          "h-full bg-secondary transition-all duration-500",
                          i < step ? "w-full" : "w-0"
                        )}
                      />
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* Form Section */}
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="p-6 pt-4 flex flex-col gap-5">

            {/* Step 0: Account Details */}
            {step === 0 && (
              <>
                <FormField
                  control={form.control}
                  name='name'
                  render={({ field }) => (
                    <FormItem className='space-y-2'>
                      <FormLabel className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t('name', { defaultValue: 'Name' })}
                      </FormLabel>
                      <div className="relative">
                        <span className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-gray-400`}>
                          <User className="w-5 h-5" />
                        </span>
                        <FormControl>
                          <Input
                            className={cn(
                              "w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl h-12 text-gray-800 dark:text-white focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-all text-sm placeholder-gray-400",
                              isRTL ? "pr-10 pl-4" : "pl-10 pr-4"
                            )}
                            placeholder={t('name_placeholder', { defaultValue: 'Your full name' })}
                            autoFocus
                            autoComplete='name'
                            {...field}
                          />
                        </FormControl>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name='phone'
                  render={({ field }) => (
                    <FormItem className='space-y-2'>
                      <FormLabel className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t('phone', { defaultValue: 'Phone' })}
                      </FormLabel>
                      <div className="relative">
                        <span className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-gray-400`}>
                          <Phone className="w-5 h-5" />
                        </span>
                        <FormControl>
                          <Input
                            type='tel'
                            className={cn(
                              "w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl h-12 text-gray-800 dark:text-white focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-all text-sm placeholder-gray-400",
                              isRTL ? "pr-10 pl-4" : "pl-10 pr-4"
                            )}
                            placeholder={t('phone_placeholder', { defaultValue: '0663278263' })}
                            autoComplete='tel'
                            {...field}
                          />
                        </FormControl>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 mt-1">
                        <Info className="w-3 h-3" />
                        {t('phone_hint', { defaultValue: 'Local (0663278263) or international (+212663278263)' })}
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name='email'
                  render={({ field }) => (
                    <FormItem className='space-y-2'>
                      <FormLabel className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t('email', { defaultValue: 'Email' })}
                      </FormLabel>
                      <div className="relative">
                        <span className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-gray-400`}>
                          <Mail className="w-5 h-5" />
                        </span>
                        <FormControl>
                          <Input
                            type='email'
                            className={cn(
                              "w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl h-12 text-gray-800 dark:text-white focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-all text-sm placeholder-gray-400",
                              isRTL ? "pr-10 pl-4" : "pl-10 pr-4"
                            )}
                            placeholder={t('email_placeholder', { defaultValue: 'you@example.com (optional)' })}
                            autoComplete='email'
                            {...field}
                          />
                        </FormControl>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name='password'
                  render={({ field }) => (
                    <FormItem className='space-y-2'>
                      <FormLabel className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t('password', { defaultValue: 'Password' })}
                      </FormLabel>
                      <div className="relative">
                        <span className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-gray-400`}>
                          <Lock className="w-5 h-5" />
                        </span>
                        <FormControl>
                          <Input
                            type={showPassword ? 'text' : 'password'}
                            className={cn(
                              "w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl h-12 text-gray-800 dark:text-white focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-all text-sm placeholder-gray-400",
                              isRTL ? "pr-10 pl-10" : "pl-10 pr-10"
                            )}
                            placeholder='••••••••'
                            autoComplete='new-password'
                            {...field}
                          />
                        </FormControl>
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className={`absolute ${isRTL ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-gray-400 hover:text-primary transition-colors`}
                        >
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 mt-1">
                        <Info className="w-3 h-3" />
                        {t('password_hint', { defaultValue: 'At least 8 characters' })}
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            {/* Step 1: Cabinet Details */}
            {step === 1 && (
              <>
                <FormField
                  control={form.control}
                  name='cabinet_name'
                  render={({ field }) => (
                    <FormItem className='space-y-2'>
                      <FormLabel className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t('cabinet_name', { defaultValue: 'Cabinet name' })}
                      </FormLabel>
                      <div className="relative">
                        <span className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-gray-400`}>
                          <Scale className="w-5 h-5" />
                        </span>
                        <FormControl>
                          <Input
                            className={cn(
                              "w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl h-12 text-gray-800 dark:text-white focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-all text-sm placeholder-gray-400",
                              isRTL ? "pr-10 pl-4" : "pl-10 pr-4"
                            )}
                            placeholder={t('cabinet_name_placeholder', { defaultValue: 'Your cabinet name' })}
                            {...field}
                          />
                        </FormControl>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="rounded-xl bg-primary/5 border border-primary/10 p-3 flex items-start gap-2">
                  <Info className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                  <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
                    {t('availability_intro', { defaultValue: 'Your account is reviewed by our team before activation. Tell us when we can reach you (working days).' })}
                  </p>
                </div>

                <FormField
                  control={form.control}
                  name='availability_days'
                  render={({ field }) => (
                    <FormItem className='space-y-2'>
                      <FormLabel className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t('availability_days', { defaultValue: 'Available days' })}
                      </FormLabel>
                      <div className="flex flex-wrap gap-2">
                        {WEEKDAYS.map((d) => {
                          const selected = (field.value || []).includes(d.value)
                          return (
                            <button
                              key={d.value}
                              type="button"
                              aria-pressed={selected}
                              onClick={() => {
                                const current = field.value || []
                                field.onChange(
                                  selected
                                    ? current.filter((v: string) => v !== d.value)
                                    : [...current, d.value]
                                )
                              }}
                              className={cn(
                                "px-4 h-11 rounded-xl border text-sm font-medium transition-all",
                                selected
                                  ? "bg-primary text-white border-primary shadow-sm"
                                  : "bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-primary/50"
                              )}
                            >
                              {d.label}
                            </button>
                          )
                        })}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name='availability_slot'
                  render={({ field }) => {
                    const SLOTS: { value: 'morning' | 'afternoon'; label: string; icon: typeof Sunrise }[] = [
                      { value: 'morning', label: t('slot_morning', { defaultValue: 'Morning' }), icon: Sunrise },
                      { value: 'afternoon', label: t('slot_afternoon', { defaultValue: 'Afternoon' }), icon: Sunset },
                    ]
                    return (
                      <FormItem className='space-y-2'>
                        <FormLabel className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t('availability_slot', { defaultValue: 'Preferred time' })}
                        </FormLabel>
                        <div className="grid grid-cols-2 gap-2">
                          {SLOTS.map((s) => {
                            const selected = field.value === s.value
                            const Icon = s.icon
                            return (
                              <button
                                key={s.value}
                                type="button"
                                aria-pressed={selected}
                                onClick={() => field.onChange(s.value)}
                                className={cn(
                                  "h-14 rounded-xl border flex items-center justify-center gap-2 text-sm font-medium transition-all",
                                  selected
                                    ? "bg-primary text-white border-primary shadow-sm"
                                    : "bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-primary/50"
                                )}
                              >
                                <Icon className="w-4 h-4" />
                                {s.label}
                              </button>
                            )
                          })}
                        </div>
                        <FormMessage />
                      </FormItem>
                    )
                  }}
                />
              </>
            )}

            {/* Step 2: Review */}
            {step === 2 && (
              <div className='space-y-4'>
                <FormField
                  control={form.control}
                  name="preferred_language"
                  render={({ field }) => (
                    <FormItem className='space-y-2'>
                      <FormLabel className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t('preferred_language')}
                      </FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl h-12">
                            <SelectValue placeholder={t('select_preferred_language')} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="ar">{t('arabic')}</SelectItem>
                          <SelectItem value="fr">{t('french')}</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className='text-sm text-gray-500 dark:text-gray-400 space-y-3'>
                  <p>{t('signup_review_message', { defaultValue: "We'll set language and timezone automatically. Review your details and submit." })}</p>
                  {(() => {
                    const v = form.watch()
                    return (
                      <div className='grid grid-cols-1 gap-2 text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-800 p-4 rounded-lg'>
                        <div>
                          <span className='text-gray-500 dark:text-gray-400'>{t('name', { defaultValue: 'Name' })}:</span> {v.name || '-'}
                        </div>
                        <div>
                          <span className='text-gray-500 dark:text-gray-400'>{t('phone', { defaultValue: 'Phone' })}:</span> {v.phone || '-'}
                        </div>
                        <div>
                          <span className='text-gray-500 dark:text-gray-400'>{t('email', { defaultValue: 'Email' })}:</span> {v.email || '-'}
                        </div>
                        <div>
                          <span className='text-gray-500 dark:text-gray-400'>{t('cabinet_name', { defaultValue: 'Cabinet name' })}:</span> {v.cabinet_name || '-'}
                        </div>
                        <div>
                          <span className='text-gray-500 dark:text-gray-400'>{t('availability_days', { defaultValue: 'Available days' })}:</span>{' '}
                          {(v.availability_days && v.availability_days.length)
                            ? v.availability_days.map((d) => t(`day_${d}`, { defaultValue: d })).join(', ')
                            : '-'}
                        </div>
                        <div>
                          <span className='text-gray-500 dark:text-gray-400'>{t('availability_slot', { defaultValue: 'Preferred time' })}:</span>{' '}
                          {v.availability_slot ? t(`slot_${v.availability_slot}`, { defaultValue: v.availability_slot }) : '-'}
                        </div>
                        <div>
                          <span className='text-gray-500 dark:text-gray-400'>{t('preferred_language', { defaultValue: 'Preferred Language' })}:</span> {v.preferred_language === 'ar' ? t('arabic') : t('french')}
                        </div>
                      </div>
                    )
                  })()}
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex flex-col gap-4 pt-2 pb-4">
              {step < 2 ? (
                <div className="flex gap-4">
                  <Button
                    type='button'
                    onClick={handleNext}
                    disabled={isLoading}
                    className='flex-1 bg-primary hover:bg-primary-dark text-white h-12 rounded-lg font-medium shadow-sm shadow-blue-200 dark:shadow-none flex items-center justify-center gap-2 group'
                  >
                    <span>{t('next', { defaultValue: 'Next' })}</span>
                    {isRTL ? (
                      <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                    ) : (
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    )}
                  </Button>
                  {step > 0 && (
                    <Button
                      type='button'
                      variant='outline'
                      onClick={() => setStep(Math.max(step - 1, 0))}
                      className='w-1/3 border-gray-200 dark:border-gray-700 h-12 rounded-lg flex items-center justify-center gap-2'
                    >
                      {isRTL ? (
                        <ArrowRight className="w-4 h-4" />
                      ) : (
                        <ArrowLeft className="w-4 h-4" />
                      )}
                      <span>{t('back', { defaultValue: 'Back' })}</span>
                    </Button>
                  )}
                </div>
              ) : (
                <div className="flex gap-4">
                  <Button
                    className='flex-1 bg-primary hover:bg-primary-dark text-white h-12 rounded-lg font-medium shadow-sm shadow-blue-200 dark:shadow-none'
                    disabled={isLoading}
                  >
                    {isLoading ? t('creating_account', { defaultValue: 'Creating account…' }) : t('signup', { defaultValue: 'Sign up' })}
                  </Button>
                  <Button
                    type='button'
                    variant='outline'
                    disabled={isLoading}
                    onClick={() => setStep(Math.max(step - 1, 0))}
                    className='w-1/3 border-gray-200 dark:border-gray-700 h-12 rounded-lg flex items-center justify-center gap-2'
                  >
                    {isRTL ? (
                      <ArrowRight className="w-4 h-4" />
                    ) : (
                      <ArrowLeft className="w-4 h-4" />
                    )}
                    <span>{t('back', { defaultValue: 'Back' })}</span>
                  </Button>
                </div>
              )}

              <div className='flex flex-col items-center gap-3 mt-2'>
                <p className='text-sm text-gray-500 dark:text-gray-400'>
                  {t('have_account', { defaultValue: 'Already have an account?' })}{' '}
                  <Link to='/auth/login' className='font-semibold text-primary hover:underline'>
                    {t('login', { defaultValue: 'Log in' })}
                  </Link>
                </p>
                <p className='text-xs text-center text-gray-500 dark:text-gray-500 max-w-xs leading-relaxed'>
                  {t('agree_privacy', { defaultValue: 'By signing up, you agree to our ' })}
                  <a href='/privacy-policy.html' target='_blank' className='underline hover:text-primary transition-colors'>
                    {t('privacy_policy', { defaultValue: 'Privacy Policy' })}
                  </a>
                </p>
              </div>
            </div>

          </form>
        </Form>
      </div>

      {/* Secured By Badge — mirrors login footer */}
      <div className="mt-8 text-gray-400 text-xs flex items-center gap-1 opacity-60">
        <Verified className="w-4 h-4" />
        <span>Secured by AvocatPro Systems</span>
      </div>
    </div>
  )
}